import React, { useEffect, useMemo, useState } from "react";
import type { User } from "firebase/auth";
import {
  Alert,
  Badge,
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  IntelligenceCapacityId,
  IntelligenceCatalogItem,
  applyIntelligenceCatalogDefaults,
  fetchIntelligenceCatalog,
  initializeIntelligenceCatalogOnStripe,
  syncIntelligenceCatalogItemStripe,
  unlinkIntelligenceCatalogItemStripe,
  updateIntelligenceCatalogItem,
} from "../../../api/intelligenceCatalog";
import "./IntelligenceGlass.css";

const CAPACITY_IDS: IntelligenceCapacityId[] = ["lite", "plus", "extra"];

/** Lean LATAM defaults — must stay aligned with server catalog.py */
const DEFAULTS: Record<IntelligenceCapacityId, IntelligenceCatalogItem> = {
  lite: {
    id: "lite",
    name: "Intelligence Lite",
    monthlyCredits: 400,
    monthlyPrice: 2.99,
    currency: "USD",
    marketingFeatures: [
      "400 créditos / mes",
      "~40 turnos de agente",
      "Ideal para probar",
    ],
    sortOrder: 1,
  },
  plus: {
    id: "plus",
    name: "Intelligence Plus",
    monthlyCredits: 1500,
    monthlyPrice: 7.99,
    currency: "USD",
    marketingFeatures: [
      "1,500 créditos / mes",
      "~150 turnos de agente",
      "Uso diario 1–2 personas",
    ],
    sortOrder: 2,
  },
  extra: {
    id: "extra",
    name: "Intelligence Extra",
    monthlyCredits: 5000,
    monthlyPrice: 19.99,
    currency: "USD",
    marketingFeatures: [
      "5,000 créditos / mes",
      "~500 turnos de agente",
      "Equipos / uso intensivo",
    ],
    sortOrder: 3,
  },
};

const LOOKUP_KEY: Record<IntelligenceCapacityId, string> = {
  lite: "intelligence_lite",
  plus: "intelligence_plus",
  extra: "intelligence_extra",
};

const stripeDashboardBase =
  process.env.REACT_APP_STRIPE_DASHBOARD_URL?.replace(/\/+$/, "") ||
  "https://dashboard.stripe.com";

interface Props {
  show: boolean;
  user: User;
  onHide: () => void;
}

const emptyDraft = (
  id: IntelligenceCapacityId
): IntelligenceCatalogItem => ({ ...DEFAULTS[id] });

export const IntelligenceCatalogModal: React.FC<Props> = ({
  show,
  user,
  onHide,
}) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [items, setItems] = useState<
    Record<IntelligenceCapacityId, IntelligenceCatalogItem>
  >(DEFAULTS);
  const [selectedId, setSelectedId] = useState<IntelligenceCapacityId | null>(
    null
  );
  const [draft, setDraft] = useState<IntelligenceCatalogItem | null>(null);
  const [monthlyPriceInput, setMonthlyPriceInput] = useState("");
  const [syncStripeOnSave, setSyncStripeOnSave] = useState(true);

  const mergeList = (list: IntelligenceCatalogItem[]) => {
    const next = { ...DEFAULTS };
    for (const item of list) {
      if (CAPACITY_IDS.includes(item.id)) next[item.id] = item;
    }
    setItems(next);
    return next;
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchIntelligenceCatalog(user);
      mergeList(list);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to load Intelligence catalog"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    setSelectedId(null);
    setDraft(null);
    setSuccess(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, user]);

  const openEdit = (id: IntelligenceCapacityId) => {
    const row = items[id] || emptyDraft(id);
    setSelectedId(id);
    setDraft({ ...row });
    setMonthlyPriceInput(
      typeof row.monthlyPrice === "number" && Number.isFinite(row.monthlyPrice)
        ? String(row.monthlyPrice)
        : ""
    );
    setError(null);
    setSuccess(null);
  };

  const closeEdit = () => {
    setSelectedId(null);
    setDraft(null);
  };

  const patchDraft = (patch: Partial<IntelligenceCatalogItem>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const marketingText = useMemo(
    () => (draft?.marketingFeatures || []).join("\n"),
    [draft?.marketingFeatures]
  );

  const estimatedTurns = useMemo(() => {
    const credits = Number(draft?.monthlyCredits || 0);
    return Math.floor(credits / 10);
  }, [draft?.monthlyCredits]);

  const runAction = async (
    label: string,
    action: () => Promise<void>
  ): Promise<void> => {
    setBusy(label);
    setError(null);
    setSuccess(null);
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const saveDraft = async (withStripe: boolean) => {
    if (!selectedId || !draft) return;
    await runAction(withStripe ? "save-sync" : "save", async () => {
      const parsedPrice = Number(monthlyPriceInput.replace(",", "."));
      if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
        throw new Error("Monthly price must be a valid non-negative number.");
      }
      const credits = Number(draft.monthlyCredits || 0);
      if (!Number.isFinite(credits) || credits < 0) {
        throw new Error("Monthly credits must be a valid non-negative number.");
      }
      const updated = await updateIntelligenceCatalogItem(user, selectedId, {
        name: draft.name,
        monthlyCredits: credits,
        monthlyPrice: parsedPrice,
        currency: (draft.currency || "USD").toUpperCase(),
        marketingFeatures: (draft.marketingFeatures || []).filter(Boolean),
        sortOrder: Number(draft.sortOrder || 0),
        stripeProductId: draft.stripeProductId || null,
        stripePriceId: draft.stripePriceId || null,
        syncStripe: withStripe,
      });
      setItems((prev) => ({ ...prev, [selectedId]: updated }));
      setDraft({ ...updated });
      setMonthlyPriceInput(String(updated.monthlyPrice));
      setSuccess(
        withStripe
          ? `Saved ${updated.name} and synced Stripe.`
          : `Saved ${updated.name} (Firestore only).`
      );
    });
  };

  const syncSelected = async () => {
    if (!selectedId) return;
    await runAction("sync", async () => {
      const updated = await syncIntelligenceCatalogItemStripe(user, selectedId);
      setItems((prev) => ({ ...prev, [selectedId]: updated }));
      setDraft({ ...updated });
      setSuccess(`Synced ${updated.name} to Stripe.`);
    });
  };

  const unlinkSelected = async () => {
    if (!selectedId) return;
    if (
      !window.confirm(
        "Clear Stripe product/price IDs from this catalog row? Stripe objects are not deleted."
      )
    ) {
      return;
    }
    await runAction("unlink", async () => {
      const updated = await unlinkIntelligenceCatalogItemStripe(
        user,
        selectedId
      );
      setItems((prev) => ({ ...prev, [selectedId]: updated }));
      setDraft({ ...updated });
      setSuccess(`Unlinked Stripe IDs for ${updated.name}.`);
    });
  };

  const initializeAll = async () => {
    await runAction("init", async () => {
      const list = await initializeIntelligenceCatalogOnStripe(user);
      const next = mergeList(list);
      if (selectedId) {
        setDraft({ ...next[selectedId] });
        setMonthlyPriceInput(String(next[selectedId].monthlyPrice));
      }
      setSuccess(`Initialized Stripe for ${list.length} packages.`);
    });
  };

  const applyLeanDefaults = async (scope: "selected" | "all") => {
    const ids =
      scope === "selected" && selectedId ? [selectedId] : CAPACITY_IDS;
    const apply = window.confirm(
      `Reset ${ids.length === 1 ? ids[0] : "all packages"} to lean prices/credits?\n\n` +
        `Lite $2.99 / 400 · Plus $7.99 / 1,500 · Extra $19.99 / 5,000`
    );
    if (!apply) return;
    const withStripe = window.confirm(
      "Also sync to Stripe after applying defaults?\n\nOK = yes · Cancel = Firestore only"
    );
    await runAction("defaults", async () => {
      const list = await applyIntelligenceCatalogDefaults(user, {
        capacityIds: ids,
        syncStripe: withStripe,
      });
      const next = mergeList(list);
      if (selectedId && next[selectedId]) {
        setDraft({ ...next[selectedId] });
        setMonthlyPriceInput(String(next[selectedId].monthlyPrice));
      }
      setSuccess(
        withStripe
          ? `Applied lean defaults and synced Stripe (${ids.join(", ")}).`
          : `Applied lean defaults (${ids.join(", ")}).`
      );
    });
  };

  const isBusy = busy !== null || loading;

  return (
    <Modal
      show={show}
      onHide={() => !isBusy && onHide()}
      centered
      size="xl"
      dialogClassName="intel-glass-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Intelligence catalog (CRUD)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <p className="intel-glass-hint">
          Manage Lite / Plus / Extra list prices, credit allowances, and Stripe
          products. Price changes create a new Stripe Price when amount differs;
          old prices stay for existing subscribers.
        </p>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            variant="primary"
            disabled={isBusy}
            onClick={() => void initializeAll()}
          >
            {busy === "init" ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Initializing…
              </>
            ) : (
              "Initialize all on Stripe"
            )}
          </Button>
          <Button
            variant="outline-primary"
            disabled={isBusy}
            onClick={() => void applyLeanDefaults("all")}
          >
            Apply lean defaults (all)
          </Button>
          <Button
            variant="outline-secondary"
            disabled={isBusy}
            onClick={() => void load()}
          >
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            Loading catalog…
          </div>
        ) : (
          <div className="table-responsive mb-3">
            <Table hover size="sm" className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Credits</th>
                  <th>Price</th>
                  <th>~Turns</th>
                  <th>Stripe</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {CAPACITY_IDS.map((id) => {
                  const row = items[id];
                  const linked = Boolean(row.stripeProductId && row.stripePriceId);
                  const selected = selectedId === id;
                  return (
                    <tr
                      key={id}
                      className={selected ? "table-active" : undefined}
                      style={{ cursor: "pointer" }}
                      onClick={() => openEdit(id)}
                    >
                      <td>
                        <strong>{row.name}</strong>
                        <div className="small text-muted">
                          <code>{id}</code> · {LOOKUP_KEY[id]}
                        </div>
                      </td>
                      <td>{Number(row.monthlyCredits || 0).toLocaleString()}</td>
                      <td>
                        {row.currency || "USD"}{" "}
                        {Number(row.monthlyPrice || 0).toFixed(2)}
                      </td>
                      <td>~{Math.floor(Number(row.monthlyCredits || 0) / 10)}</td>
                      <td>
                        {linked ? (
                          <Badge bg="success">Linked</Badge>
                        ) : (
                          <Badge bg="secondary">Not linked</Badge>
                        )}
                      </td>
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant={selected ? "primary" : "outline-primary"}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(id);
                          }}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        {draft && selectedId && (
          <div className="intel-glass-panel">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
              <div>
                <h6 className="mb-1">Edit · {draft.name}</h6>
                <div className="small text-muted">
                  Lookup key <code>{LOOKUP_KEY[selectedId]}</code>
                  {estimatedTurns > 0 && (
                    <> · ~{estimatedTurns} agent turns / mo @ 10 credits</>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={closeEdit}
                disabled={isBusy}
              >
                Close editor
              </Button>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Display name</Form.Label>
              <Form.Control
                value={draft.name}
                onChange={(e) => patchDraft({ name: e.target.value })}
                disabled={isBusy}
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Monthly credits</Form.Label>
                  <Form.Control
                    type="number"
                    min={0}
                    value={draft.monthlyCredits}
                    onChange={(e) =>
                      patchDraft({ monthlyCredits: Number(e.target.value || 0) })
                    }
                    disabled={isBusy}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Monthly price</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>{draft.currency || "USD"}</InputGroup.Text>
                    <Form.Control
                      value={monthlyPriceInput}
                      onChange={(e) => setMonthlyPriceInput(e.target.value)}
                      disabled={isBusy}
                    />
                  </InputGroup>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Control
                    value={draft.currency || "USD"}
                    onChange={(e) =>
                      patchDraft({ currency: e.target.value.toUpperCase() })
                    }
                    disabled={isBusy}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Sort order</Form.Label>
                  <Form.Control
                    type="number"
                    value={draft.sortOrder ?? 0}
                    onChange={(e) =>
                      patchDraft({ sortOrder: Number(e.target.value || 0) })
                    }
                    disabled={isBusy}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Marketing features (one per line)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={marketingText}
                    onChange={(e) =>
                      patchDraft({
                        marketingFeatures: e.target.value
                          .split("\n")
                          .map((line) => line.trim())
                          .filter(Boolean),
                      })
                    }
                    disabled={isBusy}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Stripe product ID</Form.Label>
                  <Form.Control
                    value={draft.stripeProductId || ""}
                    onChange={(e) =>
                      patchDraft({ stripeProductId: e.target.value || null })
                    }
                    placeholder="prod_…"
                    disabled={isBusy}
                  />
                  {draft.stripeProductId && (
                    <a
                      className="small"
                      href={`${stripeDashboardBase}/products/${draft.stripeProductId}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in Stripe →
                    </a>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Stripe price ID</Form.Label>
                  <Form.Control
                    value={draft.stripePriceId || ""}
                    onChange={(e) =>
                      patchDraft({ stripePriceId: e.target.value || null })
                    }
                    placeholder="price_…"
                    disabled={isBusy}
                  />
                  {draft.stripePriceId && (
                    <a
                      className="small"
                      href={`${stripeDashboardBase}/prices/${draft.stripePriceId}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in Stripe →
                    </a>
                  )}
                </Form.Group>
              </div>
            </div>

            <Form.Check
              className="mb-3"
              type="checkbox"
              id="intel-sync-stripe-on-save"
              label="Sync to Stripe when using Save + sync"
              checked={syncStripeOnSave}
              onChange={(e) => setSyncStripeOnSave(e.target.checked)}
              disabled={isBusy}
            />

            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-primary"
                disabled={isBusy}
                onClick={() => void saveDraft(false)}
              >
                {busy === "save" ? "Saving…" : "Save (Firestore)"}
              </Button>
              <Button
                variant="primary"
                disabled={isBusy || !syncStripeOnSave}
                onClick={() => void saveDraft(true)}
              >
                {busy === "save-sync" ? "Saving…" : "Save + sync Stripe"}
              </Button>
              <Button
                variant="outline-secondary"
                disabled={isBusy}
                onClick={() => void syncSelected()}
              >
                {busy === "sync" ? "Syncing…" : "Sync Stripe only"}
              </Button>
              <Button
                variant="outline-warning"
                disabled={isBusy}
                onClick={() => void unlinkSelected()}
              >
                {busy === "unlink" ? "Unlinking…" : "Unlink Stripe IDs"}
              </Button>
              <Button
                variant="outline-primary"
                disabled={isBusy}
                onClick={() => void applyLeanDefaults("selected")}
              >
                Reset this to lean defaults
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={isBusy}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
